import { CircularProgress } from '@mui/material';
import { Suspense, lazy } from 'react';
import { RequireCamEdit } from './RequireCamEdit';

const ClaimsQueuePage = lazy(() => import('../features/queue/pages/ClaimsQueuePage'));
const ClaimDetailPage = lazy(() => import('../features/detail/pages/ClaimDetailPage'));
const LinkedClaimDetailPage = lazy(() => import('../features/detail/pages/LinkedClaimDetailPage'));
const ExceptionsQueuePage = lazy(() => import('../features/exceptions/pages/ExceptionsQueuePage'));
const MissedFindingsPage = lazy(() =>
  import('../features/missedFindings').then((m) => ({ default: m.MissedFindingsPage })),
);

/**
 * Lazy route element for the claims queue. Authorization (CAM Edit) is enforced by the
 * route-level `RequireCamEdit` guard, which mounts `ClaimsQueuePage` — and its data hooks — only
 * for authorized users, so no content renders and no queue query fires before the guard evaluates
 * (FR-029).
 */
export function ClaimsQueueRouteElement() {
  return (
    <RequireCamEdit>
      <Suspense fallback={<CircularProgress data-testid="claims-suspense-fallback" />}>
        <ClaimsQueuePage />
      </Suspense>
    </RequireCamEdit>
  );
}

/**
 * Lazy route element for the US2 claim detail page. Same `RequireCamEdit` guard as the queue route
 * — no partial rendering and no detail query firing before the guard evaluates.
 */
export function ClaimDetailRouteElement() {
  return (
    <RequireCamEdit>
      <Suspense fallback={<CircularProgress data-testid="claims-suspense-fallback" />}>
        <ClaimDetailPage />
      </Suspense>
    </RequireCamEdit>
  );
}

/**
 * Lazy route element for the per-claim linked-claim detail page. Same `RequireCamEdit` guard as
 * the other claims routes — read-only, no mutation affordance.
 */
export function LinkedClaimDetailRouteElement() {
  return (
    <RequireCamEdit>
      <Suspense fallback={<CircularProgress data-testid="claims-suspense-fallback" />}>
        <LinkedClaimDetailPage />
      </Suspense>
    </RequireCamEdit>
  );
}

/**
 * Lazy route element for the Exceptions queue (US2, FR-014/FR-015). Same `RequireCamEdit` guard
 * as the other claims routes — read-only, no mutation affordance in this slice.
 */
export function ExceptionsQueueRouteElement() {
  return (
    <RequireCamEdit>
      <Suspense fallback={<CircularProgress data-testid="claims-suspense-fallback" />}>
        <ExceptionsQueuePage />
      </Suspense>
    </RequireCamEdit>
  );
}

/**
 * Lazy route element for the Missed Findings worklist (spec 204 US3, FR-022). Same `RequireCamEdit`
 * guard as the other claims routes — the page and its worklist query mount only for an authorized
 * user, so no read fires before the guard evaluates.
 */
export function MissedFindingsRouteElement() {
  return (
    <RequireCamEdit>
      <Suspense fallback={<CircularProgress data-testid="claims-suspense-fallback" />}>
        <MissedFindingsPage />
      </Suspense>
    </RequireCamEdit>
  );
}
